import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';

import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function formatNumber(num) {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString('nb-NO');
}

function formatDuration(ms) {
    if (!ms || ms <= 0) return '0s';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes === 0) return `${seconds}s`;
    return `${minutes}m ${seconds}s`;
}

function formatShortDate(dateStr) {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}.${parts[1]}`;
}

/**
 * Product statistics drawer (drawer name: 'productStats').
 * Mirrors the article stats drawer. Views come from product view drawer sessions
 * (scrollView type productView), add-to-cart and logged-in counts from usageLog events.
 */
@observer
class ProductStatsDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            stats: null,
            isLoading: true,
            error: null,
            // Default to 30 days; all-time is one click away and can be slow on popular items.
            period: '30d', // 'today', '7d', '30d', 'all', 'custom'
            customStartDate: '',
            customEndDate: '',
        };
    }

    loadStats = async (periodOverride) => {
        const { productId } = this.props;
        const { period, customStartDate, customEndDate } = this.state;
        const activePeriod = periodOverride || period;

        if (!productId) {
            this.setState({ error: 'No product ID provided', isLoading: false });
            return;
        }

        this.setState({ isLoading: true });

        try {
            let url = `/api/products/${productId}/stats?period=${activePeriod}`;
            if (activePeriod === 'custom' && customStartDate && customEndDate) {
                url += `&startDate=${customStartDate}&endDate=${customEndDate}`;
            }
            const response = await util.fetchApi(url, { method: 'GET' });
            if (response.status === 200) {
                this.setState({ stats: response.data, isLoading: false });
            } else {
                this.setState({ error: response.message || 'Failed to load stats', isLoading: false });
            }
        } catch (err) {
            this.setState({ error: err.message, isLoading: false });
        }
    }

    handlePeriodChange = (newPeriod) => {
        this.setState({ period: newPeriod }, () => {
            if (newPeriod !== 'custom') {
                this.loadStats(newPeriod);
            }
        });
    }

    handleDateChange = (field, value) => {
        this.setState({ [field]: value });
    }

    handleCustomDateSubmit = () => {
        const { customStartDate, customEndDate } = this.state;
        if (customStartDate && customEndDate) {
            this.loadStats('custom');
        }
    }

    async componentDidMount() {
        // Push queued usage events (the product_view just logged by the product drawer)
        // before loading, so the viewer's own visit is already counted.
        const { appState } = this.props.stores;
        await appState.flushUsageLog();
        this.loadStats();
    }

    renderSummaryCard(title, value, icon, darkmode, subtitle) {
        return (
            <div class='col-6 col-md-3 mb-3'>
                <div class={`rounded-lg p-3 h-100 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                    <div class='text-muted small mb-1'>
                        <i class={`fa-solid ${icon} me-1`} />{title}
                    </div>
                    <div class='d-flex align-items-baseline'>
                        <span class='h4 mb-0 font-weight-bold'>{value}</span>
                    </div>
                    {subtitle && <div class='text-muted small mt-1'>{subtitle}</div>}
                </div>
            </div>
        );
    }

    renderEngagement(engagement) {
        const views = engagement?.totalViews || 0;
        const addToCart = engagement?.addToCart || 0;

        if (views === 0 && addToCart === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    No views in this period yet
                </div>
            );
        }

        const base = Math.max(views, addToCart);
        const items = [
            { label: 'Views', value: views, color: 'bg-primary', icon: 'fa-eye' },
            { label: 'Added to cart', value: addToCart, color: 'bg-success', icon: 'fa-cart-plus' },
        ];

        return (
            <div>
                {items.map(item => {
                    const percent = base > 0 ? Math.round((item.value / base) * 100) : 0;
                    const barWidth = Math.min(Math.max(percent, item.value > 0 ? 2 : 0), 100);
                    return (
                        <div class='mb-2' key={item.label}>
                            <div class='d-flex justify-content-between small mb-1'>
                                <span><i class={`fa-solid ${item.icon} me-1`} />{item.label}</span>
                                <span class='text-muted'>{formatNumber(item.value)} ({percent}%)</span>
                            </div>
                            <div class='progress' style='height: 14px;'>
                                <div
                                    class={`progress-bar ${item.color}`}
                                    style={`width: ${barWidth}%;`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    getCountryFlag(countryCode) {
        if (!countryCode || countryCode.length !== 2) return '';
        // Convert country code to flag emoji (regional indicator symbols)
        const codePoints = countryCode
            .toUpperCase()
            .split('')
            .map(char => 127397 + char.charCodeAt(0));
        return String.fromCodePoint(...codePoints);
    }

    renderVisitorMap(cityBreakdown) {
        const cities = (cityBreakdown || []).filter(c => Number.isFinite(c.lat) && Number.isFinite(c.lon));
        if (cities.length === 0) {
            return null;
        }
        const maxVisitors = Math.max(...cities.map(c => c.visitors));
        const bounds = cities.map(c => [c.lat, c.lon]);

        return (
            <div class='rounded-lg overflow-hidden' style='height: 300px;'>
                <MapContainer
                    bounds={bounds}
                    boundsOptions={{ padding: [40, 40], maxZoom: 10 }}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={false}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                    />
                    {cities.map(c => (
                        <CircleMarker
                            key={`${c.city}-${c.countryCode}`}
                            center={[c.lat, c.lon]}
                            radius={6 + Math.round(14 * Math.sqrt(c.visitors / maxVisitors))}
                            pathOptions={{ color: '#0d6efd', fillColor: '#0d6efd', fillOpacity: 0.4 }}
                        >
                            <Popup>
                                <strong>{c.city}</strong>{c.country ? `, ${c.country}` : ''}<br />
                                {formatNumber(c.visitors)} {c.visitors === 1 ? 'visitor' : 'visitors'}
                            </Popup>
                        </CircleMarker>
                    ))}
                </MapContainer>
            </div>
        );
    }

    renderCountryBreakdown(countryBreakdown) {
        if (!countryBreakdown || countryBreakdown.length === 0) {
            return null;
        }

        const maxVisitors = Math.max(...countryBreakdown.map(c => c.visitors));

        return (
            <div>
                {countryBreakdown.map((country, idx) => {
                    const barWidth = maxVisitors > 0 ? (country.visitors / maxVisitors) * 100 : 0;
                    return (
                        <div class='mb-2' key={country.countryCode || idx}>
                            <div class='d-flex justify-content-between small mb-1'>
                                <span>
                                    {country.countryCode && <span class='me-2'>{this.getCountryFlag(country.countryCode)}</span>}
                                    {country.country}
                                </span>
                                <span class='text-muted'>{formatNumber(country.visitors)} ({country.percentage}%)</span>
                            </div>
                            <div class='progress' style='height: 8px;'>
                                <div
                                    class='progress-bar bg-primary'
                                    style={`width: ${barWidth}%;`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderCityBreakdown(cityBreakdown) {
        if (!cityBreakdown || cityBreakdown.length === 0) {
            return null;
        }

        const maxVisitors = Math.max(...cityBreakdown.map(c => c.visitors));

        return (
            <div>
                {cityBreakdown.map((city, idx) => {
                    const barWidth = maxVisitors > 0 ? (city.visitors / maxVisitors) * 100 : 0;
                    return (
                        <div class='mb-2' key={`${city.city}-${city.countryCode}` || idx}>
                            <div class='d-flex justify-content-between small mb-1'>
                                <span>
                                    {city.countryCode && <span class='me-2'>{this.getCountryFlag(city.countryCode)}</span>}
                                    {city.city}{city.country ? ` (${city.country})` : ''}
                                </span>
                                <span class='text-muted'>{formatNumber(city.visitors)} ({city.percentage}%)</span>
                            </div>
                            <div class='progress' style='height: 8px;'>
                                <div
                                    class='progress-bar bg-info'
                                    style={`width: ${barWidth}%;`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderDailyChart(dailyStats) {
        if (!dailyStats || dailyStats.length === 0) {
            return (
                <div class='text-muted text-center py-4'>
                    No daily data available yet
                </div>
            );
        }

        const dayTotal = d => (d.views || 0) + (d.addToCart || 0);
        const maxTotal = Math.max(...dailyStats.map(dayTotal));
        const displayData = dailyStats.slice(-30);
        const barWidth = Math.floor(100 / Math.min(displayData.length, 30));

        return (
            <div class='d-flex align-items-end justify-content-between' style='height: 130px; overflow-x: auto;'>
                {displayData.map((day, idx) => {
                    const total = dayTotal(day);
                    const viewsHeight = maxTotal > 0 ? ((day.views || 0) / maxTotal) * 100 : 0;
                    const cartHeight = maxTotal > 0 ? ((day.addToCart || 0) / maxTotal) * 100 : 0;
                    const dateLabel = day.date ? day.date.split('-')[2] : idx + 1;

                    return (
                        <div
                            key={idx}
                            class='d-flex flex-column align-items-center flex-shrink-0'
                            style={`width: ${barWidth}%; min-width: 12px; max-width: 24px;`}
                            title={`${day.date}: ${formatNumber(day.views)} views, ${formatNumber(day.addToCart)} added to cart (${formatNumber(total)} total)`}
                        >
                            <div class='d-flex flex-column-reverse' style='height: 100px; width: 100%;'>
                                <div class='bg-primary' style={`height: ${viewsHeight}%; min-height: ${day.views > 0 ? '2px' : '0'};`} />
                                <div class='bg-success' style={`height: ${cartHeight}%; min-height: ${day.addToCart > 0 ? '2px' : '0'};`} />
                            </div>
                            <div class='text-muted small mt-1' style='font-size: 0.55rem;'>
                                {dateLabel}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderPeakHours(peakHours) {
        if (!peakHours || peakHours.length === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    No peak hours data available
                </div>
            );
        }

        const maxViews = Math.max(...peakHours.map(p => p.views || 0));
        const chartHeight = 80;

        return (
            <div>
                <div class='d-flex align-items-end justify-content-between' style={`height: ${chartHeight}px;`}>
                    {peakHours.map((hour, idx) => {
                        const barHeight = maxViews > 0
                            ? Math.max(((hour.views || 0) / maxViews) * chartHeight, 2)
                            : 2;
                        const isPeak = hour.views === maxViews && maxViews > 0;

                        return (
                            <div
                                key={idx}
                                class='d-flex flex-column justify-content-end'
                                style={`width: 4%; min-width: 8px; height: ${chartHeight}px;`}
                                title={`${hour.hour}:00 - ${formatNumber(hour.views)} views`}
                            >
                                <div
                                    class={isPeak ? 'bg-warning' : 'bg-primary'}
                                    style={`height: ${barHeight}px; width: 100%; border-radius: 2px 2px 0 0;`}
                                />
                            </div>
                        );
                    })}
                </div>
                <div class='d-flex justify-content-between text-muted small mt-1'>
                    <span>00:00</span>
                    <span>06:00</span>
                    <span>12:00</span>
                    <span>18:00</span>
                    <span>23:00</span>
                </div>
            </div>
        );
    }

    renderWeekdays(weekdayStats) {
        if (!weekdayStats || weekdayStats.length === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    No weekday data available
                </div>
            );
        }

        const maxViews = Math.max(...weekdayStats.map(w => w.views || 0));
        const chartHeight = 80;
        const best = weekdayStats.reduce((max, w) => (w.views > max.views ? w : max), weekdayStats[0]);

        return (
            <div>
                <div class='d-flex align-items-end justify-content-around' style={`height: ${chartHeight}px;`}>
                    {weekdayStats.map(day => {
                        const barHeight = maxViews > 0
                            ? Math.max(((day.views || 0) / maxViews) * chartHeight, 2)
                            : 2;
                        const isBest = maxViews > 0 && day.views === maxViews;

                        return (
                            <div
                                key={day.isoWeekday}
                                class='d-flex flex-column justify-content-end align-items-center'
                                style={`width: 10%; height: ${chartHeight}px;`}
                                title={`${WEEKDAY_LABELS[day.isoWeekday - 1]}: ${formatNumber(day.views)} views`}
                            >
                                <div
                                    class={isBest ? 'bg-warning' : 'bg-primary'}
                                    style={`height: ${barHeight}px; width: 100%; max-width: 40px; border-radius: 2px 2px 0 0;`}
                                />
                            </div>
                        );
                    })}
                </div>
                <div class='d-flex justify-content-around text-muted small mt-1'>
                    {weekdayStats.map(day => (
                        <span key={day.isoWeekday} style='width: 10%; text-align: center;'>
                            {WEEKDAY_LABELS[day.isoWeekday - 1]}
                        </span>
                    ))}
                </div>
                {maxViews > 0 && (
                    <div class='text-muted small mt-2'>
                        <i class='fa-solid fa-lightbulb me-1' />
                        Most traffic on {WEEKDAY_LABELS[best.isoWeekday - 1]}.
                    </div>
                )}
            </div>
        );
    }

    renderPeriodSelector(darkmode) {
        const { period, customStartDate, customEndDate, isLoading } = this.state;

        const periods = [
            { key: 'today', label: 'Today' },
            { key: '7d', label: '7 days' },
            { key: '30d', label: '30 days' },
            { key: 'all', label: 'All time' },
            { key: 'custom', label: 'Custom' },
        ];

        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <div class='d-flex flex-wrap align-items-center gap-2'>
                    <span class='text-muted small me-2'>
                        <i class='fa-solid fa-filter me-1' />
                        Period:
                    </span>
                    <div class='btn-group btn-group-sm'>
                        {periods.map(p => (
                            <button
                                key={p.key}
                                type='button'
                                class={`btn ${period === p.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => this.handlePeriodChange(p.key)}
                                disabled={isLoading}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>

                    {period === 'custom' && (
                        <div class='d-flex align-items-center gap-2 ms-2'>
                            <input
                                type='date'
                                class='form-control form-control-sm'
                                style='width: 140px;'
                                value={customStartDate}
                                onChange={(e) => this.handleDateChange('customStartDate', e.target.value)}
                            />
                            <span class='text-muted'>to</span>
                            <input
                                type='date'
                                class='form-control form-control-sm'
                                style='width: 140px;'
                                value={customEndDate}
                                onChange={(e) => this.handleDateChange('customEndDate', e.target.value)}
                            />
                            <button
                                type='button'
                                class='btn btn-sm btn-primary'
                                onClick={this.handleCustomDateSubmit}
                                disabled={!customStartDate || !customEndDate || isLoading}
                            >
                                <i class='fa-solid fa-search' />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { stats, isLoading, error } = this.state;
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (isLoading && !stats) {
            return (
                <div class='w-100 d-flex justify-content-center align-items-center' style='min-height: 200px;'>
                    <div class='spinner-border text-primary' role='status'>
                        <span class='visually-hidden'>Loading...</span>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div class='w-100 p-4'>
                    <div class='alert alert-danger'>{error}</div>
                </div>
            );
        }

        if (!stats) {
            return (
                <div class='w-100 p-4'>
                    <div class='alert alert-warning'>No statistics available</div>
                </div>
            );
        }

        const {
            product, summary, engagement, dailyStats, peakHours, weekdayStats,
            deviceBreakdown, countryBreakdown, cityBreakdown,
        } = stats;
        const hasSessions = summary?.totalSessions > 0;
        const visitorMap = this.renderVisitorMap(cityBreakdown);

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding: 20px; padding-bottom: 100px;'>

                    <div class='mb-4'>
                        <h5 class='mb-1'>
                            <i class='fa-solid fa-chart-line me-2' />
                            Statistics: {product?.name}
                            {isLoading && <span class='spinner-border spinner-border-sm ms-2' />}
                        </h5>
                        {product?.productNumber && (
                            <div class='text-muted small'>
                                <i class='fa-solid fa-barcode me-1' />
                                {product.productNumber}
                            </div>
                        )}
                    </div>

                    {/* Period Selector */}
                    {this.renderPeriodSelector(darkmode)}

                    {/* Summary Cards */}
                    <div class='row'>
                        {this.renderSummaryCard('Total Views', formatNumber(summary?.totalViews), 'fa-eye', darkmode,
                            `${formatNumber(summary?.pageViews)} logged in, ${formatNumber(summary?.anonymousViews)} anonymous`)}
                        {this.renderSummaryCard('Unique Visitors', formatNumber(summary?.uniqueVisitors), 'fa-users', darkmode)}
                        {this.renderSummaryCard('Avg View Time', hasSessions ? formatDuration(summary?.avgViewDuration) : '—', 'fa-stopwatch', darkmode,
                            hasSessions ? `${formatNumber(summary.totalSessions)} sessions` : null)}
                        {this.renderSummaryCard('Added to Cart', formatNumber(summary?.addToCart), 'fa-cart-plus', darkmode,
                            summary?.totalViews > 0 ? `${summary.conversionRate}% of views` : null)}
                    </div>
                    <div class='row'>
                        {this.renderSummaryCard('Logged-in Users', formatNumber(summary?.loggedInUsers), 'fa-user-check', darkmode)}
                        {this.renderSummaryCard('Mobile', hasSessions ? `${deviceBreakdown?.mobile || 0}%` : '—', 'fa-mobile-screen', darkmode)}
                        {this.renderSummaryCard('Peak Hour', Number.isInteger(summary?.peakHour) ? `${summary.peakHour}:00` : '—', 'fa-clock', darkmode)}
                        {this.renderSummaryCard('Best Day', summary?.bestDay ? formatShortDate(summary.bestDay.date) : '—', 'fa-star', darkmode,
                            summary?.bestDay ? `${formatNumber(summary.bestDay.views)} views` : null)}
                    </div>

                    {/* Engagement */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-hand-pointer me-2' />
                            Engagement
                        </h6>
                        {this.renderEngagement(engagement)}
                    </div>

                    {/* Visitors Map */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-map-location-dot me-2' />
                            Visitors Map
                        </h6>
                        {visitorMap || (
                            <div class='text-muted text-center py-4'>
                                <i class='fa-solid fa-map-location-dot d-block mb-2' style='font-size: 1.5rem;' />
                                No visitor locations yet.<br />
                                <span class='small'>
                                    Locations come from visitor sessions saved when the product drawer closes. Super-admin visits have no location.
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Country Breakdown */}
                    {countryBreakdown?.length > 0 && (
                        <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <h6 class='mb-3'>
                                <i class='fa-solid fa-globe me-2' />
                                Visitors by Country
                            </h6>
                            {this.renderCountryBreakdown(countryBreakdown)}
                        </div>
                    )}

                    {/* City Breakdown */}
                    {cityBreakdown?.length > 0 && (
                        <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <h6 class='mb-3'>
                                <i class='fa-solid fa-city me-2' />
                                Visitors by City
                            </h6>
                            {this.renderCityBreakdown(cityBreakdown)}
                        </div>
                    )}

                    {/* Daily Traffic Chart */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <div class='d-flex justify-content-between align-items-center mb-3 flex-wrap'>
                            <h6 class='mb-0'>
                                <i class='fa-solid fa-chart-bar me-2' />
                                Daily Traffic (Last 30 Days)
                            </h6>
                            <div class='d-flex gap-3 small'>
                                <span><span class='badge bg-primary'>&nbsp;</span> Views</span>
                                <span><span class='badge bg-success'>&nbsp;</span> Added to cart</span>
                            </div>
                        </div>
                        {this.renderDailyChart(dailyStats)}
                    </div>

                    {/* Peak Hours Chart */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-clock me-2' />
                            Peak Hours (Views by Hour)
                        </h6>
                        {this.renderPeakHours(peakHours)}
                    </div>

                    {/* Best Weekday */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-calendar-days me-2' />
                            Traffic by Weekday
                        </h6>
                        {this.renderWeekdays(weekdayStats)}
                    </div>

                </div>
            </div>
        );
    }
}

export default ProductStatsDrawer;
